import kebabCase from 'lodash/kebabCase';
import React, { Fragment } from 'react';
import { generateKeyForElement } from '../../../../../helpers/elementKeyHelper';
import Link, { ButtonLink } from '../../../../Link';
import {
  isCardSection,
  NavLink,
  NavSectionWithCtas,
  NestedPrimaryNavItem,
} from '../../../types';
import withOnClick from '../../../withOnClick';
import { primarySectionProps, secondarySectionProps } from './layout';
import {
  DummySecondarySection,
  LinkWrapper,
  Section,
  SectionHeading,
  SectionLinks,
} from './styles';

type NestedNavMenuProps = {
  navItem: NestedPrimaryNavItem;
  activeNestedItemIndex: number | null;
  setActiveNestedItemIndex: (newIndex: number | null) => void;
  resetNav: () => void;
};

const getSubsectionId = (name: string, index: number): string =>
  `${kebabCase(name)}-subsection-${index}`;

const NestedNavMenu: React.FC<NestedNavMenuProps> = ({
  navItem,
  activeNestedItemIndex,
  setActiveNestedItemIndex,
  resetNav,
}) => {
  const activeNestedItem =
    activeNestedItemIndex !== null &&
    navItem.primarySection.subsections[activeNestedItemIndex];

  const onClick = (
    event: React.MouseEvent,
    subsection: NavSectionWithCtas,
    index: number
  ): void => {
    if (subsection.onExpand && activeNestedItemIndex !== index) {
      subsection.onExpand(event);
    }
    setActiveNestedItemIndex(index);
  };

  return (
    <>
      {/* Primary section (with interactive items) */}
      <Section {...primarySectionProps}>
        <SectionHeading>{navItem.primarySection.heading}</SectionHeading>
        <SectionLinks>
          {navItem.primarySection.subsections.map((subsection, index) => (
            <LinkWrapper key={subsection.name}>
              <ButtonLink
                onClick={(event) => onClick(event, subsection, index)}
                aria-expanded={activeNestedItemIndex === index}
                aria-haspopup
                aria-controls={subsection.sections
                  .map((_, i) => getSubsectionId(subsection.name, i))
                  .join(' ')}>
                {subsection.name}
              </ButtonLink>
            </LinkWrapper>
          ))}
        </SectionLinks>
      </Section>
      {/* Subsections (if selected) */}
      {activeNestedItem ? (
        activeNestedItem.sections.map(
          (navSection, index) =>
            !isCardSection(navSection) && (
              <Fragment key={generateKeyForElement(navSection)}>
                {activeNestedItem.sections.length === 1 && index === 0 && (
                  <DummySecondarySection />
                )}
                <Section
                  {...secondarySectionProps}
                  id={getSubsectionId(activeNestedItem.name, index)}>
                  <SectionHeading>{navSection.heading}</SectionHeading>
                  <SectionLinks>
                    {navSection.links.map((link: NavLink) => (
                      <LinkWrapper key={generateKeyForElement(link)}>
                        <Link
                          removeVisitedStyle
                          href={link.url}
                          onClick={withOnClick(link, resetNav).onClick}>
                          {link.text}
                        </Link>
                      </LinkWrapper>
                    ))}
                  </SectionLinks>
                </Section>
              </Fragment>
            )
        )
      ) : (
        <>
          <DummySecondarySection />
          <DummySecondarySection />
        </>
      )}
    </>
  );
};

export default NestedNavMenu;
